import type { CountdownProps, JumuahProps, RamadanProps, Widget } from '@noorview/core';
import { addDays, clockDuration, compactDuration, formatDate, hhmm, hijriFor, nextJumuah, ymd, zonedToInstant } from '@noorview/core';
import { el, setText, sv, wall12 } from './dom';
import { countdownTarget } from './designs';
import type { Env, Tick, WidgetView } from './widgets';

/**
 * Timer faces shared by every widget that counts down to a moment: Countdown, Jumu'ah, Iftar, Sehri and Tarawih.
 * Each widget supplies a source (what it counts to, from when, and the words around it); the face draws it.
 * Rings, bars and arcs show how much of the wait has gone, from `from` to `at`.
 */

export interface Timed {
  /** Heading, e.g. "Asr jama'ah in" or "Iftar". */
  label: string;
  /** Short name, e.g. "Asr" or "Iftar". */
  what: string;
  at: Date;
  from: Date;
  /** The target as wall time, already in the widget's 12/24-hour setting. */
  time: string;
  /** The usual line under the main figure, e.g. "at 14:49" or "Tomorrow at 5:30". */
  sub: string;
  /** Ends of the bar, e.g. ["Sehri 05:30", "Iftar 17:30"]. */
  ends?: [string, string];
  /** An extra line some widgets add (e.g. "Ramadan 12"). */
  extra?: string;
}
export interface TimedSource {
  bind(w: Widget): void;
  get(t: Tick, env: Env): Timed | null;
  /** Heading and hint when there's nothing to count to (the hint is hidden on the real screen). */
  empty(env: Env): { label: string; hint: string };
}
export type FaceKind = 'digital' | 'ring' | 'hourglass' | 'flip' | 'bar' | 'words' | 'arc';

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const left = (tm: Timed, now: Date) => tm.at.getTime() - now.getTime();
/** "1:14:53" for waits under a day; "1d 23h" beyond (a 47-hour clock is hard to read). */
const span = (ms: number, end = '00:00') => (ms <= 0 ? end : ms >= 86_400_000 ? compactDuration(ms) : clockDuration(ms));
const gone = (tm: Timed, now: Date) => clamp01((now.getTime() - tm.from.getTime()) / Math.max(1, tm.at.getTime() - tm.from.getTime()));

/** "2 hours 5 minutes", "12 minutes", "40 seconds", "3 days 4 hours". */
export function wordsDuration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  const u = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
  if (d > 0) return h ? `${u(d, 'day')} ${u(h, 'hour')}` : u(d, 'day');
  if (h > 0) return m ? `${u(h, 'hour')} ${u(m, 'minute')}` : u(h, 'hour');
  if (m > 0) return u(m, 'minute');
  return u(s, 'second');
}

/** Flip digits (the same cards as the flip clock). */
function flipDigit() {
  const root = el('div', 'fl');
  const top = el('div', 'fl-h fl-top'), bot = el('div', 'fl-h fl-bot'), ft = el('div', 'fl-h fl-top fl-ft'), fb = el('div', 'fl-h fl-bot fl-fb');
  const span = (p: HTMLElement) => { const s = el('span'); p.append(s); return s; };
  const [t, b, f1, f2] = [span(top), span(bot), span(ft), span(fb)];
  root.append(top, bot, ft, fb);
  let cur = '';
  fb.addEventListener('animationend', () => { b.textContent = cur; root.classList.remove('go'); });
  return {
    root,
    set(v: string, animate: boolean) {
      if (v === cur) return;
      const old = cur;
      cur = v;
      if (!animate || !old) { t.textContent = b.textContent = f1.textContent = f2.textContent = v; return; }
      f1.textContent = old; b.textContent = old; t.textContent = v; f2.textContent = v;
      root.classList.remove('go');
      void root.offsetWidth;
      root.classList.add('go');
    },
  };
}

export function timerFace(c: HTMLElement, kind: FaceKind, src: TimedSource): WidgetView {
  const label = el('div', 'nvd-label'), big = el('div', 'big'), sub = el('div', 'nvd-sub'), extra = el('div', 'nvd-sub nvd-acc');
  let update: (tm: Timed | null, t: Tick, env: Env) => boolean;

  if (kind === 'ring') {
    const wrap = el('div', 'ring-w');
    const svg = sv('svg', { viewBox: '0 0 200 200', class: 'ring', 'aria-hidden': 'true' });
    const arc = sv('circle', { cx: 100, cy: 100, r: 88, class: 'ring-arc' });
    svg.append(sv('circle', { cx: 100, cy: 100, r: 88, class: 'ring-track' }), arc);
    const inner = el('div', 'ring-in');
    inner.append(label, big, sub, extra);
    wrap.append(svg, inner);
    c.append(wrap);
    const C = 2 * Math.PI * 88;
    arc.setAttribute('stroke-dasharray', `${C} ${C}`);
    update = (tm, t) => {
      if (!tm) { arc.style.strokeDashoffset = String(C); return false; }
      arc.style.strokeDashoffset = String(C * gone(tm, t.now));
      return setText(big, span(left(tm, t.now)));
    };
  } else if (kind === 'hourglass') {
    const wrap = el('div', 'hg-w');
    const svg = sv('svg', { viewBox: '0 0 120 200', class: 'hg', 'aria-hidden': 'true' });
    const uid = `hg${Math.random().toString(36).slice(2, 8)}`;
    const glassTop = 'M22 18h76c0 34-22 52-34 66-2 3-6 3-8 0C44 70 22 52 22 18z', glassBot = 'M22 182h76c0-34-22-52-34-66-2-3-6-3-8 0C44 130 22 148 22 182z';
    const top = sv('clipPath', { id: `${uid}t` }, sv('path', { d: glassTop })), bot = sv('clipPath', { id: `${uid}b` }, sv('path', { d: glassBot }));
    const sandTop = sv('rect', { x: 0, y: 18, width: 120, height: 70, class: 'hg-sand' }), sandBot = sv('rect', { x: 0, y: 112, width: 120, height: 70, class: 'hg-sand' });
    const stream = sv('line', { x1: 60, y1: 84, x2: 60, y2: 180, class: 'hg-stream' });
    svg.append(sv('defs', {}, top, bot), sv('g', { 'clip-path': `url(#${uid}t)` }, sandTop), sv('g', { 'clip-path': `url(#${uid}b)` }, sandBot), stream,
      sv('path', { d: glassTop, class: 'hg-glass' }), sv('path', { d: glassBot, class: 'hg-glass' }),
      sv('rect', { x: 12, y: 8, width: 96, height: 10, rx: 5, class: 'hg-cap' }), sv('rect', { x: 12, y: 182, width: 96, height: 10, rx: 5, class: 'hg-cap' }));
    const meta = el('div', 'hg-meta');
    meta.append(label, big, sub, extra);
    wrap.append(svg, meta);
    c.append(wrap);
    update = (tm, t, env) => {
      wrap.classList.toggle('side', env.box.width / env.box.height > 1.1);
      if (!tm) { stream.style.opacity = '0'; return false; }
      const f = 1 - gone(tm, t.now);
      (sandTop as SVGRectElement).style.transform = `translateY(${((1 - f) * 66).toFixed(2)}px)`;
      (sandBot as SVGRectElement).style.transform = `translateY(${(f * 66).toFixed(2)}px)`;
      stream.style.opacity = left(tm, t.now) > 0 ? '1' : '0';
      return setText(big, span(left(tm, t.now)));
    };
  } else if (kind === 'flip') {
    const wrap = el('div', 'nvd-center'), row = el('div', 'fl-row');
    wrap.append(label, row, sub, extra);
    c.append(wrap);
    const digits: Array<ReturnType<typeof flipDigit>> = [], units: HTMLElement[] = [];
    for (let g = 0; g < 3; g++) {
      if (g) row.append(el('span', 'fl-colon', ':'));
      const grp = el('div', 'fl-g'), dd = el('div', 'fl-gd'), u = el('div', 'fl-u');
      for (let i = 0; i < 2; i++) { const d = flipDigit(); digits.push(d); dd.append(d.root); }
      grp.append(dd, u);
      units.push(u);
      row.append(grp);
    }
    let first = true;
    update = (tm, t, env) => {
      const s = tm ? Math.max(0, Math.floor(left(tm, t.now) / 1000)) : 0;
      // Hours (up to 99), minutes, seconds. Longer waits show days in the line underneath.
      // Over a day: days, hours and minutes. Otherwise hours, minutes and seconds.
      const long = s >= 86_400;
      const a = long ? Math.min(99, Math.floor(s / 86_400)) : Math.floor(s / 3600), b = long ? Math.floor((s % 86_400) / 3600) : Math.floor((s % 3600) / 60), c2 = long ? Math.floor((s % 3600) / 60) : s % 60;
      const txt = `${String(a).padStart(2, '0')}${String(b).padStart(2, '0')}${String(c2).padStart(2, '0')}`;
      (long ? ['Days', 'Hours', 'Mins'] : ['Hours', 'Mins', 'Secs']).forEach((x, i) => { units[i]!.textContent = x; });
      const anim = !first && env.mode !== 'thumbnail';
      digits.forEach((d, i) => d.set(txt[i]!, anim));
      first = false;
      return false;
    };
  } else if (kind === 'bar') {
    const wrap = el('div', 'tb'), head = el('div', 'tb-head'), track = el('div', 'tb-track'), fill = el('i'), ends = el('div', 'tb-ends'), e0 = el('span'), e1 = el('span');
    head.append(label, big);
    track.append(fill);
    ends.append(e0, e1);
    wrap.append(head, track, ends, sub, extra);
    c.append(wrap);
    update = (tm, t) => {
      if (!tm) { fill.style.transform = 'scaleX(0)'; ends.style.display = 'none'; return false; }
      fill.style.transform = `scaleX(${gone(tm, t.now).toFixed(4)})`;
      ends.style.display = tm.ends ? '' : 'none';
      let ch = false;
      if (tm.ends) { ch = setText(e0, tm.ends[0]) || ch; ch = setText(e1, tm.ends[1]) || ch; }
      // The end of the bar already names the time, so the line underneath only adds which day it is.
      sub.style.display = tm.ends && tm.sub === `at ${tm.time}` ? 'none' : '';
      return setText(big, span(left(tm, t.now), 'Now')) || ch;
    };
  } else if (kind === 'words') {
    const wrap = el('div', 'nvd-center tw');
    big.classList.add('tw-big');
    wrap.append(label, big, sub, extra);
    c.append(wrap);
    update = (tm, t) => (tm ? setText(big, left(tm, t.now) > 0 ? wordsDuration(left(tm, t.now)) : 'Now') : false);
  } else if (kind === 'arc') {
    // The sun (or moon) travelling across the sky from `from` to `at`, with the time underneath.
    const wrap = el('div', 'sa');
    const svg = sv('svg', { viewBox: '0 0 220 116', class: 'sa-svg', 'aria-hidden': 'true' });
    const path = 'M14 104 A96 96 0 0 1 206 104';
    const done = sv('path', { d: path, class: 'sa-done' });
    const body = sv('circle', { cx: 14, cy: 104, r: 9, class: 'sa-sun' });
    svg.append(sv('path', { d: path, class: 'sa-track' }), done, sv('line', { x1: 4, y1: 104, x2: 216, y2: 104, class: 'sa-hz' }), body);
    const meta = el('div', 'sa-meta');
    meta.append(label, big, sub, extra);
    wrap.append(svg, meta);
    c.append(wrap);
    const L = Math.PI * 96;
    done.setAttribute('stroke-dasharray', `${L} ${L}`);
    update = (tm, t) => {
      const f = tm ? gone(tm, t.now) : 0;
      done.style.strokeDashoffset = String(L * (1 - f));
      const a = Math.PI * (1 - f);
      body.setAttribute('cx', (110 + 96 * Math.cos(a)).toFixed(2));
      body.setAttribute('cy', (104 - 96 * Math.sin(a)).toFixed(2));
      if (!tm) return false;
      const ms = left(tm, t.now);
      let ch = setText(big, tm.time);
      ch = setText(sub, ms > 0 ? `in ${compactDuration(ms)}` : 'Now') || ch;
      return ch;
    };
  } else {
    const wrap = el('div', 'nvd-center');
    wrap.append(label, big, sub, extra);
    c.append(wrap);
    update = (tm, t) => (tm ? setText(big, span(left(tm, t.now))) : false);
  }

  return {
    update(w) { src.bind(w); },
    tick(t, env) {
      const tm = src.get(t, env);
      let ch = false;
      if (!tm) {
        const e = src.empty(env);
        ch = setText(label, e.label) || ch;
        if (kind !== 'flip') ch = setText(big, '—') || ch;
        sub.style.display = '';
        ch = setText(sub, env.mode === 'screen' ? '' : e.hint) || ch;
        extra.style.display = 'none';
        return update(null, t, env) || ch;
      }
      ch = setText(label, kind === 'words' ? `Until ${tm.what}` : kind === 'arc' ? tm.what : tm.label) || ch;
      if (kind !== 'arc') { sub.style.display = ''; ch = setText(sub, kind === 'words' ? `at ${tm.time}` : tm.sub) || ch; }
      extra.style.display = tm.extra ? '' : 'none';
      ch = setText(extra, tm.extra ?? '') || ch;
      return update(tm, t, env) || ch;
    },
    destroy() {},
  };
}

/* ---------- Sources ---------- */


/** Countdown widget: the next prayer (start or jama'ah) or a date the masjid sets. */
export function countdownSource(): TimedSource {
  let p: CountdownProps;
  return {
    bind(w) { p = w.props as CountdownProps; },
    get(t, env) {
      const tg = countdownTarget(p, t, env);
      if (!tg) return null;
      const tz = env.masjid.timezone, time = hhmm(tg.at, tz);
      const what = p.target === 'custom' ? (p.customLabel || p.label || 'Event') : tg.label.replace(/ in$/, '');
      const sameDay = ymd(tg.from, tz) === ymd(tg.at, tz);
      return { label: tg.label, what, at: tg.at, from: tg.from, time, sub: `at ${time}`, ends: [sameDay ? hhmm(tg.from, tz) : formatDate(tg.from, tz, 'short'), sameDay ? time : formatDate(tg.at, tz, 'short')] };
    },
    empty() { return { label: p.label || 'Countdown', hint: '' }; },
  };
}

/** Jumu'ah: counts to the next Friday's first jama'ah, over the week since the last one. */
export function jumuahSource(): TimedSource {
  let p: JumuahProps;
  return {
    bind(w) { p = w.props as JumuahProps; },
    get(t, env) {
      const n = nextJumuah(env.masjid, env.timetable, t.now);
      if (!n) return null;
      const tz = env.masjid.timezone, s0 = n.slots[0]!, at = zonedToInstant(n.day, s0.jamaah, tz);
      const today = n.day === ymd(t.now, tz);
      const slots = n.slots.length > 1 ? n.slots.map((s, i) => `${i === 0 ? '1st' : '2nd'} ${s.jamaah}`).join(' · ') : s0.khutbah ? `Khutbah ${s0.khutbah} · Jama‘ah ${s0.jamaah}` : `Jama‘ah ${s0.jamaah}`;
      return {
        label: p.title || 'Jumu‘ah', what: 'Jumu‘ah', at, from: new Date(at.getTime() - 7 * 86_400_000), time: s0.jamaah,
        sub: today ? `Today · ${slots}` : slots, ends: ['Last Friday', today ? 'Today' : formatDate(at, tz, 'short')],
      };
    },
    empty() { return { label: p.title || 'Jumu‘ah', hint: 'Add Jumu‘ah times to your timetable' }; },
  };
}

const RAMADAN_TITLE = { iftar: 'Iftar', sehri: 'Sehri ends', tarawih: 'Tarawih' } as const;

/**
 * Iftar (Maghrib), Sehri ends and Tarawih, from the timetable. Rings and bars show the fast (Sehri to Iftar), the
 * night (Iftar to Sehri) or the evening (Iftar to Tarawih).
 */
export function ramadanSource(kind: 'iftar' | 'sehri' | 'tarawih'): TimedSource {
  let p: RamadanProps;
  const timeOf = (row: { maghrib?: string; sehri?: string; tarawih?: string; fajr?: string } | undefined, k: typeof kind) => (k === 'iftar' ? row?.maghrib : k === 'sehri' ? row?.sehri : row?.tarawih);
  return {
    bind(w) { p = w.props as RamadanProps; },
    get(t, env) {
      const tz = env.masjid.timezone, today = ymd(t.now, tz);
      for (let i = 0; i < 60; i++) {
        const d = addDays(today, i), row = env.timetable.get(d), tt = timeOf(row, kind);
        if (!tt) continue;
        const at = zonedToInstant(d, tt, tz);
        if (at.getTime() <= t.now.getTime() - 5 * 60_000) continue;
        // Where the wait starts: Iftar from that day's Sehri (or Fajr); Sehri from the evening before; Tarawih from Iftar.
        const prev = env.timetable.get(addDays(d, -1));
        const fromT = kind === 'iftar' ? row?.sehri || row?.fajr : kind === 'sehri' ? prev?.maghrib : row?.maghrib;
        const fromDay = kind === 'sehri' ? addDays(d, -1) : d;
        const from = fromT ? zonedToInstant(fromDay, fromT, tz) : new Date(at.getTime() - 12 * 3600_000);
        const fromName = kind === 'iftar' ? (row?.sehri ? 'Sehri' : 'Fajr') : 'Iftar';
        const when = d === today ? '' : d === addDays(today, 1) ? 'Tomorrow ' : `${formatDate(at, tz, 'short')} · `;
        const h = hijriFor(today, env.masjid.prayer.hijriAdjust);
        const title = p.title || RAMADAN_TITLE[kind];
        return {
          label: title, what: title, at, from, time: wall12(tt, p.h12), sub: `${when}at ${wall12(tt, p.h12)}`,
          ends: [`${fromName} ${wall12(fromT, p.h12)}`, `${kind === 'sehri' ? 'Sehri' : title} ${wall12(tt, p.h12)}`],
          extra: p.showDay && h.month === 9 ? `Ramadan ${h.day}` : undefined,
        };
      }
      return null;
    },
    empty() {
      return { label: p.title || RAMADAN_TITLE[kind], hint: kind === 'iftar' ? 'Add Maghrib times to your timetable' : `Add ${kind === 'sehri' ? 'Sehri Ends' : 'Tarawih'} times to your timetable for Ramadan` };
    },
  };
}
